import { useToast } from "@/components/toast-provider";
import { Routes } from "@/constants/routes";
import { createProduct, deleteProduct, deleteProductImages, updateProduct, type ProductResponse } from "@/infrastructure/services/productService";
import { defaultProductValues, type ProductFormMode, type ProductFormValues, type SubmitIntent } from "@/pages/adicionar-produtos/types";
import { buildConfiguredImages, getPositionedImages, getRemovedImageIds, mapProductToUpdateRequest } from "@/utils/product-images";
import type { BaseSyntheticEvent } from "react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useProductImageUpload } from "./useProductImageUpload";

type Options = { mode?: ProductFormMode; productId?: number; initialValues?: ProductFormValues; initialProduct?: ProductResponse };

export function useProductForm({ mode = "create", productId, initialValues, initialProduct }: Options = {}) {
  const methods = useForm<ProductFormValues>({ defaultValues: initialValues ?? defaultProductValues, mode: "onBlur" });
  const [submitIntent, setSubmitIntent] = useState<SubmitIntent | null>(null);
  const { uploadAndConfirmImages } = useProductImageUpload();
  const { showErrorToast, showSuccessToast } = useToast();
  const navigate = useNavigate();
  useEffect(() => { if (initialValues) methods.reset(initialValues); }, [initialValues, methods]);

  const submit = async (values: ProductFormValues, event?: BaseSyntheticEvent) => {
    let createdId: number | null = null;
    const intent: SubmitIntent = ((event?.nativeEvent as SubmitEvent | undefined)?.submitter as HTMLButtonElement | null)?.value === "publish" ? "publish" : "draft";
    const status = intent === "publish" ? "PUBLICADO" as const : "RASCUNHO" as const;
    setSubmitIntent(intent);
    try {
      if (mode === "edit") {
        if (!productId || !initialValues || !initialProduct) throw new Error("Dados iniciais do produto não encontrados.");
        const newImages = getPositionedImages(values).filter(({ image }) => Boolean(image.file));
        const uploadedIds = await uploadAndConfirmImages(productId, newImages);
        await updateProduct(productId, { nome: values.name, descricao: values.description, status, categoriaId: Number(values.category), nichoId: Number(values.subcategory), palavras_chave: values.keywords, imagens: buildConfiguredImages(values, uploadedIds) });
        await deleteProductImages(productId, getRemovedImageIds(initialValues, values));
        showSuccessToast("Produto atualizado com sucesso.");
      } else {
        const product = await createProduct({ nome: values.name, descricao: values.description, preco: 0, status, categoriaId: Number(values.category), nichoId: Number(values.subcategory), palavrasChave: values.keywords });
        createdId = product.id;
        await uploadAndConfirmImages(product.id, getPositionedImages(values));
        showSuccessToast(intent === "publish" ? "Produto publicado com sucesso." : "Rascunho salvo com sucesso.");
      }
      navigate(Routes.MY_PRODUCTS);
    } catch (error) {
      if (createdId !== null) { try { await deleteProduct(createdId); } catch { showErrorToast("Não foi possível remover o produto incompleto."); } }
      if (mode === "edit" && productId && initialProduct) { try { await updateProduct(productId, mapProductToUpdateRequest(initialProduct)); } catch { showErrorToast("Não foi possível concluir nem desfazer a atualização do produto."); setSubmitIntent(null); return; } }
      showErrorToast(error instanceof Error ? error.message : mode === "edit" ? "Não foi possível atualizar o produto." : "Não foi possível cadastrar o produto.");
    } finally { setSubmitIntent(null); }
  };
  return { isSubmitting: Boolean(submitIntent), methods, submit, submitIntent };
}
